import React, { useEffect, useState } from "react";

import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import {
  LuAlertCircle,
  LuCalendarDays,
  LuCalendarRange,
  LuPower,
  LuPowerOff,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getRecordDays,
  getRecordDaysEnabled,
  setRecordDays,
} from "../../features/appSettings/appSettingsSlice";
import { PUT } from "../../services/axios.service";
import { iconSelectStyles, toastStyles } from "../../static/styles";

interface IFormInput {
  recordDaysEnabled: boolean;
  recordDays: string;
}

const maxRecordDays = 3650;

const recordDaysEnabledOptions = [
  { value: true, label: "હા, દિવસ પ્રમાણે" },
  { value: false, label: "ના, બધા રેકોર્ડ" },
];

const toRecordDays = (value: string | null | undefined) => {
  const text = String(value ?? "").trim();

  if (text === "") return null;

  const days = Number(text);

  return Number.isInteger(days) && days >= 1 && days <= maxRecordDays
    ? days
    : null;
};

const RecordDaysForm = () => {
  const dispatch = useAppDispatch();
  const recordDaysEnabled = useAppSelector(getRecordDaysEnabled);
  const recordDays = useAppSelector(getRecordDays);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<IFormInput>({
    mode: "onSubmit",
    defaultValues: { recordDaysEnabled: false, recordDays: "" },
  });

  const watchEnabled = watch("recordDaysEnabled");

  useEffect(() => {
    reset({
      recordDaysEnabled,
      recordDays: recordDays !== null ? String(recordDays) : "",
    });
  }, [recordDaysEnabled, recordDays, reset]);

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data) => {
    setIsLoading(true);

    try {
      await PUT("/v1/admin/settings/record-days", {
        recordDaysEnabled: data.recordDaysEnabled,
        recordDays: toRecordDays(data.recordDays),
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            const savedEnabled = !!response.data.data.recordDaysEnabled;
            const savedDays = Number(response.data.data.recordDays) || 0;
            const saved = savedDays > 0 ? savedDays : null;

            dispatch(
              setRecordDays({
                recordDaysEnabled: savedEnabled,
                recordDays: saved,
              })
            );
            reset({
              recordDaysEnabled: savedEnabled,
              recordDays: saved !== null ? String(saved) : "",
            });

            toast.success(response.data.message, { ...(toastStyles as any) });
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(
            error?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
      <div className="flex flex-col space-y-1.5 p-6">
        <div className="text-2xl font-bold text-[#113F67] leading-none tracking-tight flex items-center gap-2">
          <LuCalendarRange className="h-6 w-6 text-[#34699A]" />
          રેકોર્ડ દિવસ
        </div>
        <div className="text-sm text-gray-600">
          જ્યારે ચાલુ હોય, ત્યારે Super Admin સિવાયના બધા યુઝર્સને મટીરીયલ
          ઓર્ડર/ખરીદી, કટીંગ ઓર્ડર/રિસીવ, ટર્નિંગ ઓર્ડર/રિસીવ અને VMC ઓર્ડર/રિસીવ ના લિસ્ટ તથા
          રિપોર્ટ
          એક્સપોર્ટમાં ફક્ત
          આજ સહિત છેલ્લા નક્કી કરેલા દિવસોના રેકોર્ડ જ દેખાશે.
          ગણતરી રેકોર્ડની તારીખ પ્રમાણે થશે.
        </div>
      </div>

      <div className="p-6 pt-0">
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#113F67]">
                સ્ટેટ્સ <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                {watchEnabled ? (
                  <LuPower className="absolute left-4 z-[1] top-3.5 h-5 w-5 text-[#34699A]" />
                ) : (
                  <LuPowerOff className="absolute left-4 z-[1] top-3.5 h-5 w-5 text-[#34699A]" />
                )}
                <Controller
                  name="recordDaysEnabled"
                  control={control}
                  render={({ field }) => (
                    <ReactSelect
                      value={
                        recordDaysEnabledOptions.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      options={recordDaysEnabledOptions}
                      placeholder="પસંદ કરો"
                      isSearchable={false}
                      styles={iconSelectStyles as any}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      onChange={(option: any) => {
                        field.onChange(option?.value);

                        if (!option?.value) clearErrors("recordDays");
                      }}
                    />
                  )}
                />
              </div>

              <p className="text-gray-500 text-sm mt-1">
                Super Admin ને હંમેશા બધા રેકોર્ડ દેખાશે.
              </p>
            </div>

            <div
              className={`space-y-2 ${
                watchEnabled ? "" : "opacity-60 pointer-events-none"
              }`}
            >
              <label
                htmlFor="recordDays-recordDays"
                className="text-sm font-semibold text-[#113F67]"
              >
                દિવસ
                {watchEnabled && <span className="text-red-500"> *</span>}
              </label>

              <div className="relative">
                <LuCalendarDays className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="recordDays-recordDays"
                  type="number"
                  step="any"
                  inputMode="numeric"
                  placeholder="દિવસ દાખલ કરો"
                  {...register("recordDays", {
                    validate: (value, formValues) => {
                      if (!formValues.recordDaysEnabled) return true;

                      if (String(value ?? "").trim() === "")
                        return "દિવસ દાખલ કરો.";

                      return (
                        toRecordDays(value) !== null ||
                        `1 થી ${maxRecordDays} વચ્ચે પૂરા દિવસ દાખલ કરો.`
                      );
                    },
                  })}
                  className={`flex w-full bg-white px-3 py-2 ring-offset-white placeholder:text-[hsl(215_16%_47%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-12 pr-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
                    errors.recordDays
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                />
                {errors.recordDays && (
                  <LuAlertCircle className="absolute right-4 top-4 h-5 w-5 text-red-500" />
                )}
              </div>

              {errors.recordDays ? (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.recordDays.message}
                </p>
              ) : (
                <p className="text-gray-500 text-sm mt-1">
                  દા.ત. 45 દાખલ કરવાથી આજ સહિત છેલ્લા 45 દિવસના રેકોર્ડ દેખાશે.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none h-10 px-6 bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-white font-semibold text-base rounded-lg transition-all duration-300 transform hover:scale-[1.01] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  સેવ થઈ રહ્યું છે...
                </div>
              ) : (
                "સેવ કરો"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default RecordDaysForm;
